import type React from 'react';

import { Button, Text } from '@change/ds-core-ui';
import { TrendingIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, TranslateHtml } from '@change-corgi/core-react/i18n';

import { MAX_DURATION_SEC } from '../../../VideoRecorder/components/RecorderTimeLimit';
import { useOpenCameraButton } from '../OpenCameraButton/hooks/useOpenCameraButton';
import { useSkipRecording } from '../SkipRecording/hooks/useSkipRecording';
import { BoltIcon, VideocamIcon } from '../VideoFirstAsk/icons';

import { SupporterFan } from './components/SupporterFan';
import { useStaticFacesAsk } from './hooks/useStaticFacesAsk';

const STAGGER_MS = 160;

const entranceDelay = (step: number): React.CSSProperties => ({ animationDelay: `${step * STAGGER_MS}ms` });

/**
 * R2's V3 (`v3_static_faces`, CHANGE-99359). Unlike R1's `VideoFirstAsk`, downstream is production
 * for every arm: this screen owns only the ask itself, not the recorder or the preview, and the
 * primary CTA advances to the recorder rather than opening the camera from here (permission is asked
 * there, as it is for control).
 */
export const StaticFacesAsk: React.FC = () => {
	const {
		data: { shouldAnimateEntrance },
	} = useStaticFacesAsk();
	const {
		actions: { handleButtonClick },
	} = useOpenCameraButton();
	const {
		actions: { handleSkip },
	} = useSkipRecording();

	// `motion-reduce` is required, not decorative: the keyframes' `both` fill-mode would otherwise hold
	// the elements invisible.
	const entrance = shouldAnimateEntrance && 'animate-rise-in motion-reduce:animate-none';

	return (
		<div className="bg-background-base -mx-4 -mb-8 flex flex-col" data-testid="static-faces-ask">
			{/*
			 * Absolute against the page container, not this screen, so it reaches up behind the post-sign
			 * header (the layout renders the compact header for this ask, `useIsStaticFacesAskVisible`).
			 * That is also why this screen renders outside the action card - the card is `relative` and
			 * `overflow-hidden`, so a gradient inside it could never clear the header.
			 */}
			<div className="bg-gradient-background-light-2 absolute inset-x-0 top-0 z-0 h-[298px]" aria-hidden>
				<div className="to-background-base absolute inset-x-0 bottom-0 h-[77px] bg-linear-to-b from-transparent" />
			</div>

			<div className="relative z-10 flex flex-col items-center gap-3 px-4 pt-2 pb-4 text-center">
				<Text
					as="h1"
					variant="heading"
					size="x-large"
					fontWeight="bold"
					className={cn('text-typography-strong text-pretty', entrance)}
					style={shouldAnimateEntrance ? entranceDelay(0) : undefined}
				>
					<TranslateHtml value="corgi.petition_actions.r2_ask.v3_static_faces.headline" />
				</Text>
				<Text
					as="p"
					variant="body"
					size="medium"
					className={cn('text-typography-strong w-[294px] max-w-full text-pretty', entrance)}
					style={shouldAnimateEntrance ? entranceDelay(1) : undefined}
				>
					<TranslateHtml value="corgi.petition_actions.r2_ask.v3_static_faces.supporting_line" />
				</Text>
			</div>

			<div className={cn(entrance)} style={shouldAnimateEntrance ? entranceDelay(2) : undefined}>
				<SupporterFan />

				<div className="elevation-elevated bg-background-base rounded-medium relative z-10 mx-auto -mt-2.5 flex w-[237px] flex-col items-center gap-1 px-4 py-6 text-center">
					<BoltIcon className="h-5 w-5" />
					<Text variant="body" size="small" className="text-typography-strong">
						<TranslateHtml
							value="corgi.petition_actions.r2_ask.v3_static_faces.value_prop"
							replacements={{ maxDuration: MAX_DURATION_SEC }}
						/>
					</Text>
				</div>
			</div>

			{/*
			 * In flow, not pinned to the foot of the viewport - same reasoning as `VideoFirstAsk`'s dock
			 * (CHANGE-97687 review, 2026-08-25).
			 */}
			<div
				className={cn('relative z-10 mt-6 flex flex-col items-center px-4 pt-2 pb-5', entrance)}
				style={shouldAnimateEntrance ? entranceDelay(3) : undefined}
			>
				<Button
					data-testid="create-video"
					data-qa="create-video"
					variant="primary"
					size="large"
					className="w-full"
					leadingIcon={<VideocamIcon />}
					onClick={handleButtonClick}
				>
					<Translate value="corgi.petition_actions.invite.open_camera_button.label" />
				</Button>
				<Button
					data-testid="create-comment"
					data-qa="create-comment"
					variant="secondary"
					size="large"
					className="mt-3 w-full"
					onClick={handleSkip}
				>
					<Translate value="corgi.petition_actions.invite.write_button.label" />
				</Button>
				<div className="mt-3 flex items-center">
					<TrendingIcon className="mr-2" size={24} gradient />
					<Text variant="body" size="small" className="text-typography-strong">
						<Translate value="corgi.petition_actions.invite.bottom_note" />
					</Text>
				</div>
			</div>
		</div>
	);
};
